import _ from "underscore";

import {
  MAX_SERIES,
  getBreakoutSeriesName,
  getComputedSettingsForSeries,
} from "metabase/viz-core";
import type {
  DatasetColumn,
  RawSeries,
  RowValue,
  SingleSeries,
} from "metabase-types/api";
import { getRowsForStableKeys } from "metabase-types/api";

// TODO: this series transformation is used only for the visualization settings computation which is excessive.
// Replace this with defining settings models per visualization type which will contain all necessary info
// about the dataset sufficient to compute settings defaults like series settings.
export function transformSeries(series: RawSeries): RawSeries {
  const newSeries: SingleSeries[] = series.flatMap((s, seriesIndex) =>
    transformSingleSeries(s, series, seriesIndex),
  );

  if (newSeries.length === 0 || _.isEqual(series, newSeries)) {
    return series;
  }

  return newSeries;
}

type RowWithOrigin = RowValue[] & {
  _origin?: {
    seriesIndex: number;
    rowIndex: number;
    row: RowValue[];
    cols: DatasetColumn[];
  };
};

function transformSingleSeries(
  s: SingleSeries,
  series: RawSeries,
  seriesIndex: number,
): SingleSeries[] {
  const { card, data } = s;

  // @ts-expect-error HACK: prevents cards from being transformed too many times
  if (data._transformed) {
    return [s];
  }

  const { cols, rows } = data;
  const rowsForBreakoutKeys = getRowsForStableKeys(data);
  const settings = getComputedSettingsForSeries([s]);

  const dimensions = (settings["graph.dimensions"] || []).filter(
    (d) => d != null,
  );
  const metrics = (settings["graph.metrics"] || []).filter((d) => d != null);
  const dimensionColumnIndexes = dimensions.map((dimensionName) =>
    _.findIndex(cols, (col) => col.name === dimensionName),
  );
  const metricColumnIndexes = metrics.map((metricName) =>
    _.findIndex(cols, (col) => col.name === metricName),
  );
  const bubbleColumnIndex = settings["scatter.bubble"]
    ? _.findIndex(cols, (col) => col.name === settings["scatter.bubble"])
    : -1;
  const extraColumnIndexes =
    bubbleColumnIndex != null && bubbleColumnIndex >= 0
      ? [bubbleColumnIndex]
      : [];

  if (dimensions.length > 1) {
    const [dimensionColumnIndex, seriesColumnIndex] = dimensionColumnIndexes;
    const rowColumnIndexes = [dimensionColumnIndex].concat(
      metricColumnIndexes,
      extraColumnIndexes,
    );

    const breakoutValues: RowValue[] = [];
    const breakoutRowsByValue = new Map<RowValue, RowWithOrigin[]>();

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex];
      const seriesValue = rowsForBreakoutKeys[rowIndex][seriesColumnIndex];

      let seriesRows = breakoutRowsByValue.get(seriesValue);
      if (!seriesRows) {
        breakoutRowsByValue.set(seriesValue, (seriesRows = []));
        breakoutValues.push(seriesValue);

        if (breakoutValues.length > MAX_SERIES) {
          return [s];
        }
      }

      const newRow: RowWithOrigin = rowColumnIndexes.map(
        (columnIndex) => row[columnIndex],
      );
      newRow._origin = { seriesIndex, rowIndex, row, cols };
      seriesRows.push(newRow);
    }

    return breakoutValues.map((breakoutValue) => ({
      ...s,
      card: {
        ...card,
        // if multiseries include the card title as well as the breakout value
        name: getBreakoutSeriesName(
          breakoutValue,
          cols[seriesColumnIndex],
          series.length > 1,
          card.name,
        ),
        originalCardName: card.name,
        _breakoutValue: breakoutValue,
        _breakoutColumn: cols[seriesColumnIndex],
      },
      data: {
        rows: breakoutRowsByValue.get(breakoutValue) ?? [],
        cols: rowColumnIndexes.map((i) => cols[i]),
        results_metadata: data.results_metadata,
        rows_truncated: data.rows_truncated,
        native_form: data.native_form,
        results_timezone: data.results_timezone,
        _rawCols: cols,
        _transformed: true,
      },
      // for when the legend header for the breakout is clicked
      clicked: {
        dimensions: [
          {
            value: breakoutValue,
            column: cols[seriesColumnIndex],
          },
        ],
      },
    }));
  } else {
    // dimensions.length <= 1
    const dimensionColumnIndex = dimensionColumnIndexes[0];
    return metricColumnIndexes.map((metricColumnIndex) => {
      const col = cols[metricColumnIndex];
      const rowColumnIndexes = [dimensionColumnIndex].concat(
        metricColumnIndex,
        extraColumnIndexes,
      );
      const name = [
        // show series title if it's multiseries
        series.length > 1 && card.name,
        // show column name if there are multiple metrics or single series
        (metricColumnIndexes.length > 1 || series.length === 1) &&
          col?.display_name,
      ]
        .filter((n) => n)
        .join(": ");

      return {
        ...s,
        card: {
          ...card,
          name: name,
          originalCardName: card.name,
          _seriesIndex: seriesIndex,
          // use underlying column name as the seriesKey since it should be unique
          // EXCEPT for dashboard multiseries, so check seriesIndex == 0
          _seriesKey: seriesIndex === 0 && col ? col.name : name,
        },
        data: {
          rows: rows.map((row, rowIndex) => {
            const newRow: RowWithOrigin = rowColumnIndexes.map((i) => row[i]);
            newRow._origin = { seriesIndex, rowIndex, row, cols };
            return newRow;
          }),
          cols: rowColumnIndexes.map((i) => cols[i]),
          results_metadata: data.results_metadata,
          rows_truncated: data.rows_truncated,
          native_form: data.native_form,
          results_timezone: data.results_timezone,
          _transformed: true,
          _rawCols: cols,
        },
      };
    });
  }
}
